import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePopper } from 'react-popper'
import maxSize from 'popper-max-size-modifier'

import { selectIsBrowsingCannedReplies } from 'selectors/modals_base'
import { doHideCannedReplyModal, doShowCannedReplyModal } from 'actions/modals'
import ListenToKeyboard from 'components/ListenToKeyboard'
import Menu from './Menu'
import Trigger from './Trigger'

const Dropup = ({ ticketId, upward = true, isNote, isReply, ...rest }) => {
  const [triggerElement, setTriggerElement] = useState(null)
  const [popperElement, setPopperElement] = useState(null)
  const dispatch = useDispatch()
  const open = useSelector(selectIsBrowsingCannedReplies)

  // NOTE (jscheel): These must be memoized to prevent popper from getting into
  // an infinite loop for some reason.
  const modifiers = useMemo(() => {
    return [
      maxSize,
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
      {
        name: 'flip',
        options: {
          flipVariations: false, // true by default
        },
      },
      {
        name: 'applyMaxSize',
        enabled: true,
        phase: 'beforeWrite',
        requires: ['maxSize'],
        fn: ({ state }) => {
          const { height } = state.modifiersData.maxSize
          const maxHeight = Math.min(height - 8, 430)
          // eslint-disable-next-line no-param-reassign
          state.styles.popper.maxHeight = `${maxHeight}px`
          // eslint-disable-next-line no-param-reassign
          state.styles.popper.minHeight = `${Math.min(430, maxHeight)}px`
        },
      },
    ]
  }, [])

  const { styles, attributes } = usePopper(triggerElement, popperElement, {
    placement: upward ? 'top-end' : 'bottom-end',
    strategy: 'fixed',
    modifiers,
  })

  const handleTriggerClick = useCallback(
    () => {
      if (open) {
        dispatch(doHideCannedReplyModal())
      } else {
        dispatch(doShowCannedReplyModal({ isReply, isNote }))
      }
    },
    [dispatch, open, isReply, isNote]
  )

  const handleDocClick = useCallback(
    e => {
      if (
        !popperElement.contains(e.target) &&
        !triggerElement.contains(e.target)
      ) {
        dispatch(doHideCannedReplyModal())
      }
    },
    [popperElement, triggerElement, dispatch]
  )

  useEffect(
    () => {
      if (open) {
        document.addEventListener('mousedown', handleDocClick)
      } else {
        document.removeEventListener('mousedown', handleDocClick)
      }
      return () => {
        document.removeEventListener('mousedown', handleDocClick)
      }
    },
    [open, handleDocClick]
  )

  const positioningStyle = useMemo(
    () => {
      return {
        ...styles.popper,
        display: 'flex',
        zIndex: 2147483647,
      }
    },
    [styles.popper]
  )

  return (
    <React.Fragment>
      <Trigger
        forwardedRef={setTriggerElement}
        active={open}
        {...rest}
        onClick={handleTriggerClick}
      />
      {open && (
        <div
          ref={setPopperElement}
          style={positioningStyle}
          {...attributes.popper}
        >
          <ListenToKeyboard
            stopPropagation
            preventDefault
            onEsc={handleTriggerClick}
          />
          <Menu ticketId={ticketId} isNote={isNote} isReply={isReply} />
        </div>
      )}
    </React.Fragment>
  )
}

export default Dropup
